import React, { useState, useEffect, useRef } from 'react';
import './VariableAutocomplete.css';

/**
 * VariableAutocomplete Component
 * Provides autocomplete functionality for workflow variables
 * 
 * Usage:
 * <VariableAutocomplete
 *   value={textValue}
 *   onChange={(newValue) => setTextValue(newValue)}
 *   onCursorChange={(cursorPos) => handleCursorChange(cursorPos)}
 *   availableVariables={variables}
 *   placeholder="Enter text with {{variables}}"
 * />
 */
const VariableAutocomplete = ({
  value = '',
  onChange,
  onCursorChange,
  availableVariables = {},
  placeholder = '',
  rows = 1,
  className = '',
  showHelper = true,
  ...inputProps
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [variableContext, setVariableContext] = useState(null);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const isTextarea = rows > 1;

  // Get all available variables
  const getAllVariables = () => {
    const allVars = [];
    
    // Check if availableVariables is valid
    if (!availableVariables || typeof availableVariables !== 'object') {
      console.debug('VariableAutocomplete: availableVariables is not valid', availableVariables);
      return allVars;
    }
    
    // Contact fields from API
    if (availableVariables.contactFields?.variables) {
      const variableKeys = Object.keys(availableVariables.contactFields.variables);
      console.debug('VariableAutocomplete: Found contactFields variables', variableKeys.length);
      variableKeys.forEach(key => {
        const variable = availableVariables.contactFields.variables[key];
        allVars.push({
          label: variable.description || key,
          value: key,
          fullPath: key,
          category: 'contactFields',
          displayValue: `{{${key}}}`,
          example: variable.example,
          usage: variable.usage,
        });
      });
    } else {
      console.debug('VariableAutocomplete: No contactFields.variables found', {
        hasContactFields: !!availableVariables.contactFields,
        availableVariablesKeys: Object.keys(availableVariables),
      });
    }
    
    // Step results from API (stepResults.byStepType)
    // Note: These are templates that need step-id to be complete
    // They will be added dynamically from previousResults below
    
    // Event variables (for contact_updated trigger)
    if (availableVariables.event && Array.isArray(availableVariables.event)) {
      console.debug('VariableAutocomplete: Found event variables', availableVariables.event.length);
      availableVariables.event.forEach(variable => {
        allVars.push({
          ...variable,
          fullPath: variable.value,
          category: 'event',
          displayValue: `{{${variable.value}}}`,
        });
      });
    } else {
      console.debug('VariableAutocomplete: No event variables found', {
        hasEvent: !!availableVariables.event,
        eventType: typeof availableVariables.event,
        isArray: Array.isArray(availableVariables.event),
      });
    }
    
    // Previous results (from workflow nodes - dynamic)
    Object.keys(availableVariables).forEach(category => {
      // Handle step_ format (legacy)
      if (category.startsWith('step_')) {
        const stepVars = availableVariables[category];
        if (Array.isArray(stepVars)) {
          stepVars.forEach(variable => {
            allVars.push({
              ...variable,
              fullPath: variable.value,
              category: category,
              displayValue: `{{${variable.value}}}`,
            });
          });
        }
      }
      // Handle previousResults. format (new)
      if (category.startsWith('previousResults.')) {
        const stepVars = availableVariables[category];
        if (Array.isArray(stepVars)) {
          stepVars.forEach(variable => {
            allVars.push({
              ...variable,
              fullPath: variable.value,
              category: category,
              displayValue: `{{${variable.value}}}`,
            });
          });
        }
      }
      // Handle contact array (legacy format)
      if (category === 'contact' && Array.isArray(availableVariables[category])) {
        availableVariables[category].forEach(variable => {
          allVars.push({
            ...variable,
            fullPath: variable.value,
            category: 'contact',
            displayValue: `{{${variable.value}}}`,
          });
        });
      }
    });
    
    return allVars;
  };

  // Find variable context at cursor position
  const findVariableContext = (text, cursorPos) => {
    // Look backwards from cursor to find {{
    let pos = cursorPos - 1;
    let foundOpen = false;
    let openStart = -1;
    
    // Find the start of {{ 
    while (pos >= 0) {
      if (text[pos] === '}') {
        // If we find }}, check if it's a complete variable before our position
        if (pos > 0 && text[pos - 1] === '}') {
          // Found }}, check if there's a {{ before it
          let checkPos = pos - 2;
          while (checkPos >= 0 && text[checkPos] !== '{') {
            if (text[checkPos] === '}') {
              // Another closing brace, stop
              break;
            }
            checkPos--;
          }
          // If we found {{ before }}, this variable is already closed
          if (checkPos > 0 && text[checkPos] === '{' && text[checkPos - 1] === '{') {
            // This variable is closed, continue searching before it
            pos = checkPos - 2;
            continue;
          }
        }
        // Single }, no variable context
        return null;
      }
      
      if (text[pos] === '{') {
        // Check if it's part of {{
        if (pos > 0 && text[pos - 1] === '{') {
          // Found {{
          openStart = pos - 1;
          foundOpen = true;
          break;
        }
        // Single {, not valid
        return null;
      }
      
      pos--;
    }
    
    if (!foundOpen || openStart < 0) {
      return null;
    }
    
    // We found {{, now check if there's a closing }} after the cursor
    let closingPos = cursorPos;
    let foundClosing = false;
    
    while (closingPos < text.length) {
      if (text[closingPos] === '}') {
        if (closingPos < text.length - 1 && text[closingPos + 1] === '}') {
          // Found }}
          foundClosing = true;
          break;
        }
      }
      if (text[closingPos] === '{') {
        // Another opening brace before closing, invalid
        break;
      }
      closingPos++;
    }
    
    // Extract variable name (from after {{ to cursor or to }})
    const variableEnd = foundClosing ? closingPos : cursorPos;
    const variableName = text.substring(openStart + 2, variableEnd).trim();
    
    // Return context even if not closed yet (user is still typing)
    return {
      start: openStart,
      end: foundClosing ? closingPos + 2 : cursorPos,
      variableName,
      fullMatch: foundClosing ? text.substring(openStart, closingPos + 2) : text.substring(openStart, cursorPos),
      isOpen: !foundClosing, // Track if variable is not yet closed
    };
  };

  // Handle input change
  const handleChange = (e) => {
    const newValue = e.target.value;
    const newCursorPos = e.target.selectionStart;
    
    onChange(newValue);
    
    if (onCursorChange) {
      onCursorChange(newCursorPos);
    }
    
    // Check if we're typing inside {{ }}
    const context = findVariableContext(newValue, newCursorPos);
    
    if (context) {
      setVariableContext(context);
      // Filter suggestions based on partial variable name
      const allVars = getAllVariables();
      console.debug('VariableAutocomplete: Found', allVars.length, 'total variables');
      
      // If variableName is empty (just {{), show all variables
      // Otherwise, filter by the partial name
      const filtered = context.variableName.trim() === '' 
        ? allVars 
        : allVars.filter(v => 
            v.fullPath.toLowerCase().includes(context.variableName.toLowerCase()) ||
            v.label.toLowerCase().includes(context.variableName.toLowerCase())
          );
      
      console.debug('VariableAutocomplete: Filtered to', filtered.length, 'variables');
      setSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
      setSelectedIndex(0);
    } else {
      setShowSuggestions(false);
      setVariableContext(null);
    }
  };

  // Handle key down
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      if (e.key === 'Enter') {
        e.preventDefault();
      }
      insertVariable(suggestions[selectedIndex]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  // Insert variable at cursor position
  const insertVariable = (variable) => {
    if (!variable || !variableContext) return;
    
    // If variable is open (not closed with }}), replace from start to cursor
    // Otherwise, replace the entire {{variable}} block
    const before = value.substring(0, variableContext.start);
    const after = variableContext.isOpen 
      ? value.substring(variableContext.end) 
      : value.substring(variableContext.end);
    
    const newValue = before + variable.displayValue + after;
    
    onChange(newValue);
    setShowSuggestions(false);
    setVariableContext(null);
    
    // Set cursor position after inserted variable
    setTimeout(() => {
      if (inputRef.current) {
        const newCursorPos = variableContext.start + variable.displayValue.length;
        if (isTextarea) {
          inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
        } else {
          inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
        }
        inputRef.current.focus();
      }
    }, 0);
  };

  // Handle suggestion click
  const handleSuggestionClick = (variable) => {
    insertVariable(variable);
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const InputComponent = isTextarea ? 'textarea' : 'input';
  const inputType = isTextarea ? undefined : 'text';

  return (
    <div className={`variable-autocomplete-wrapper ${className}`}>
      <InputComponent
        {...inputProps}
        ref={inputRef}
        type={inputType}
        rows={isTextarea ? rows : undefined}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={(e) => {
          if (inputProps.onFocus) inputProps.onFocus(e);
          // Check if there's a variable context on focus
          const context = findVariableContext(value, e.target.selectionStart);
          if (context) {
            setVariableContext(context);
            const allVars = getAllVariables();
            const filtered = context.variableName.trim() === ''
              ? allVars
              : allVars.filter(v => 
                  v.fullPath.toLowerCase().includes(context.variableName.toLowerCase())
                );
            setSuggestions(filtered);
            setShowSuggestions(filtered.length > 0);
          }
        }}
        placeholder={placeholder}
        className={`form-control ${inputProps.className || ''}`}
      />
      
      {showSuggestions && suggestions.length > 0 && (
        <div ref={suggestionsRef} className="variable-suggestions">
          <div className="variable-suggestions-header">
            <i className="ri-code-s-slash-line me-2"></i>
            <strong>Variables disponibles</strong>
          </div>
          {suggestions.map((variable, index) => (
            <div
              key={index}
              className={`variable-suggestion-item ${index === selectedIndex ? 'selected' : ''}`}
              onClick={() => handleSuggestionClick(variable)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <div className="variable-suggestion-main">
                <code className="variable-name">{variable.displayValue}</code>
                <span className="variable-label">{variable.label}</span>
              </div>
              {variable.example && (
                <div className="variable-example">
                  <small className="text-muted">Ejemplo: {variable.example}</small>
                </div>
              )}
              {variable.category && (
                <div className="variable-category">
                  <small className="badge bg-secondary">{variable.category}</small>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      
      {showHelper && (
        <div className="variable-helper">
          <small className="text-muted">
            <i className="ri-information-line me-1"></i>
            Escribe <code>{'{{'}</code> para ver variables disponibles. Usa las flechas para navegar y Enter para insertar.
          </small>
        </div>
      )}
    </div>
  );
};

export default VariableAutocomplete;

